// Glue between the framework-agnostic engine and Vue reactivity.
// Owns: the worker, the frame pump (backpressure + adaptive cadence), the voting
// engine, and the UI state machine: priming -> scanning -> candidate -> locked.

import { reactive, ref, shallowRef, type Ref } from 'vue';
import { CameraEngine, type CameraState } from '../../engine/camera/cameraEngine';
import {
  displayRectToFrame,
  normalizeRect,
  zoomCropRect,
} from '../../engine/camera/viewTransform';
import { iou } from '../../engine/pipeline/detector';
import { MODELS, TOTAL_MODEL_BYTES } from '../../engine/pipeline/modelManifest';
import { VotingEngine, type VoteState } from '../../engine/vote/votingEngine';
import { supportsVibrate } from '../../engine/capabilities';
import { recordScan, updateScan } from '../../engine/history/db';
import type {
  ExecutionProvider,
  NormRect,
  PipelineResult,
  Rect,
  ScanMode,
  ScanRecord,
  WorkerToMain,
} from '../../engine/types';
import { TUNING } from '../../engine/types';

export type UiPhase =
  | 'boot' // loading models / creating sessions
  | 'priming' // asking for camera permission
  | 'denied' // permission refused
  | 'scanning' // live, includes candidate ghosting
  | 'locked' // frozen with result card
  | 'error';

export interface DebugInfo {
  ep: ExecutionProvider | '-';
  threads: number;
  crossOriginIsolated: boolean;
  detectorFps: number;
  e2eFps: number;
  timings: PipelineResult['timings'] | null;
  frameW: number;
  frameH: number;
  recentRaw: { raw: string; conf: number; accepted: boolean }[];
  voteTable: { pos: number; votes: { ch: string; share: number }[] }[];
}

export interface LockedResult {
  text: string;
  region: string | undefined;
  confidence: number;
  savedId: number | null;
  favorite: boolean;
}

const query = new URLSearchParams(location.search);
export const DEBUG = query.get('debug') === '1';
const FORCE_EP = query.get('ep') as ExecutionProvider | null;
const RECTIFY = query.get('rectify') !== '0'; // ladder step 2 on by default; ?rectify=0 for baseline

export function useScanner(videoRef: Ref<HTMLVideoElement | null>) {
  const phase = ref<UiPhase>('boot');
  const mode = ref<ScanMode>('auto');
  const errorMsg = ref('');
  const modelProgress = ref(0); // 0..1
  const camera = shallowRef<CameraEngine | null>(null);
  const camState = ref<CameraState | null>(null);
  const vote = ref<VoteState | null>(null);
  const locked = ref<LockedResult | null>(null);
  const suggestGuide = ref(false);
  const lockFlash = ref(false);
  /** Latest detection box + processed-frame dims, for the overlay reticle. */
  const overlay = reactive({
    box: null as Rect | null,
    frameW: 0,
    frameH: 0,
    quad: null as PipelineResult['quad'] | null,
    at: 0,
  });
  const debug = reactive<DebugInfo>({
    ep: '-',
    threads: 0,
    crossOriginIsolated: false,
    detectorFps: 0,
    e2eFps: 0,
    timings: null,
    frameW: 0,
    frameH: 0,
    recentRaw: [],
    voteTable: [],
  });
  /** Guide-box rect in display coords; kept in sync by ScanView. */
  const guideRectDisplay = ref<Rect | null>(null);
  const elemSize = reactive({ w: 0, h: 0 });

  const voting = new VotingEngine();
  let worker: Worker | null = null;
  let workerReady = false;
  let inFlight = false;
  let frameId = 0;
  let pumpStopped = false;
  let lastSubmitAt = 0;
  let totalEma = 50;
  let lastResultAt = 0;
  let lastDetectionAt = 0;
  let prevBox: Rect | null = null;
  let lowFpsSince: number | null = null;
  const progressPerUrl = new Map<string, number>();

  function initWorker(): void {
    worker = new Worker(new URL('../../engine/pipeline/worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (ev: MessageEvent<WorkerToMain>) => onWorkerMsg(ev.data);
    worker.onerror = (e) => {
      phase.value = 'error';
      errorMsg.value = `Worker failed: ${e.message}`;
    };
    worker.postMessage({
      t: 'init',
      detectorUrl: MODELS.detector.url,
      ocrUrl: MODELS.ocr.url,
      forceEp: FORCE_EP ?? undefined,
    });
  }

  function onWorkerMsg(msg: WorkerToMain): void {
    switch (msg.t) {
      case 'progress': {
        progressPerUrl.set(msg.url, msg.loaded);
        let sum = 0;
        for (const v of progressPerUrl.values()) sum += v;
        modelProgress.value = Math.min(1, sum / TOTAL_MODEL_BYTES);
        break;
      }
      case 'ready':
        workerReady = true;
        debug.ep = msg.ep;
        debug.threads = msg.threads;
        debug.crossOriginIsolated = msg.crossOriginIsolated;
        if (phase.value === 'boot') phase.value = 'priming';
        break;
      case 'result':
        onResult(msg);
        break;
      case 'error':
        if (msg.stage === 'init') {
          phase.value = 'error';
          errorMsg.value = msg.message;
        } else if (DEBUG) {
          console.warn('[autospy] frame error:', msg.message);
        }
        inFlight = false;
        break;
    }
  }

  function onResult(r: PipelineResult): void {
    inFlight = false;
    const now = performance.now();
    if (lastResultAt > 0) {
      const inst = 1000 / (now - lastResultAt);
      debug.e2eFps = debug.e2eFps * 0.8 + inst * 0.2;
      if (mode.value === 'auto') debug.detectorFps = debug.e2eFps;
    }
    lastResultAt = now;
    totalEma = totalEma * 0.7 + r.timings.total * 0.3;
    debug.timings = r.timings;
    debug.frameW = r.frameW;
    debug.frameH = r.frameH;

    overlay.frameW = r.frameW;
    overlay.frameH = r.frameH;
    overlay.quad = r.quad ?? null;

    if (mode.value === 'auto') {
      if (r.detection) {
        // §6.6: a different candidate taking over resets the vote.
        if (prevBox && iou(prevBox, r.detection.box) < TUNING.stickyIou) voting.notifyTrackLost();
        prevBox = r.detection.box;
        overlay.box = r.detection.box;
        overlay.at = now;
        lastDetectionAt = now;
      } else if (now - lastDetectionAt > TUNING.resetAfterMs) {
        if (overlay.box) voting.notifyTrackLost(); // box lost
        overlay.box = null;
        prevBox = null;
      }
      trackFpsForSuggestion(now);
    }

    if (r.read && phase.value === 'scanning') {
      const state = voting.addRead(r.read, now);
      vote.value = state;
      if (DEBUG) {
        debug.recentRaw = [...voting.recentRaw];
        debug.voteTable = voting.voteTable();
      }
      if (state.status === 'locked') onLock(state);
    }
  }

  function trackFpsForSuggestion(now: number): void {
    if (debug.detectorFps > 0 && debug.detectorFps < TUNING.guideSuggestFps) {
      lowFpsSince ??= now;
      if (now - lowFpsSince > 5000) suggestGuide.value = true;
    } else {
      lowFpsSince = null;
    }
  }

  /** Last auto-recorded scan, for dedupe and favorite/edit updates. */
  let lastSaved: ScanRecord | null = null;

  function onLock(state: VoteState): void {
    phase.value = 'locked';
    const result: LockedResult = {
      text: state.text,
      region: state.region?.name,
      confidence: state.confidence,
      savedId: null,
      favorite: false,
    };
    locked.value = result;
    // Every lock is recorded automatically (bugfix.md #1). Re-locking the same
    // plate within a minute (rescan of the same car) reuses the existing record.
    if (lastSaved?.id !== undefined && lastSaved.text === state.text && Date.now() - lastSaved.ts < 60_000) {
      result.savedId = lastSaved.id;
      result.favorite = lastSaved.favorite;
    } else {
      const rec: ScanRecord = {
        text: state.text,
        region: state.region?.name,
        confidence: state.confidence,
        ts: Date.now(),
        edited: false,
        favorite: false,
      };
      void recordScan(rec).then((saved) => {
        lastSaved = saved;
        result.savedId = saved.id ?? null;
        result.favorite = saved.favorite;
      });
    }
    videoRef.value?.pause(); // freeze the preview
    if (supportsVibrate()) navigator.vibrate(50);
    else {
      lockFlash.value = true; // iOS Safari: visual flash instead
      setTimeout(() => (lockFlash.value = false), 350);
    }
  }

  // ---- frame pump ----

  function pump(): void {
    const video = videoRef.value;
    if (!video || pumpStopped) return;
    const schedule = () => {
      if (pumpStopped) return;
      if ('requestVideoFrameCallback' in video) {
        video.requestVideoFrameCallback(() => pump());
      } else {
        requestAnimationFrame(() => pump());
      }
    };
    if (phase.value !== 'scanning' || !workerReady || inFlight || video.readyState < 2) {
      schedule();
      return;
    }
    // Adaptive cadence: never submit faster than the pipeline can drain (thermal headroom).
    const now = performance.now();
    const minInterval = Math.min(600, Math.max(0, totalEma * 0.8));
    if (now - lastSubmitAt < minInterval) {
      schedule();
      return;
    }
    lastSubmitAt = now;
    inFlight = true;
    createImageBitmap(video)
      .then((bitmap) => {
        const roi = currentRoi();
        worker?.postMessage(
          {
            t: 'frame',
            bitmap,
            mode: mode.value,
            roi,
            // Perspective rectification straightens angled plates before OCR. It
            // runs in guide mode too now: an angled plate aligned in the guide
            // box gets the same correction (rectify returns null and falls back
            // to the plain crop when it finds no plate quad, so it is safe).
            rectify: RECTIFY,
            frameId: ++frameId,
          },
          [bitmap],
        );
      })
      .catch(() => {
        inFlight = false;
      });
    schedule();
  }

  /** ROI sent to the worker, normalized to the full frame. */
  function currentRoi(): NormRect | undefined {
    const cam = camera.value;
    if (!cam) return undefined;
    const digital = cam.digitalZoom;
    if (mode.value === 'guide') {
      const g = guideRectDisplay.value;
      const s = cam.state;
      if (!g || elemSize.w === 0 || s.frameW === 0) return undefined;
      const frameRect = displayRectToFrame(g, {
        frameW: s.frameW,
        frameH: s.frameH,
        elemW: elemSize.w,
        elemH: elemSize.h,
        zoom: digital,
        mirrored: s.mirrored,
      });
      return normalizeRect(frameRect, s.frameW, s.frameH);
    }
    return digital > 1.001 ? zoomCropRect(digital) : undefined;
  }

  // ---- public actions ----

  async function begin(): Promise<void> {
    initWorker();
  }

  async function requestCamera(): Promise<void> {
    const video = videoRef.value;
    if (!video) return;
    const cam = new CameraEngine(video);
    camera.value = cam;
    cam.onChange((s) => (camState.value = s));
    try {
      await cam.start();
      phase.value = 'scanning';
      pumpStopped = false;
      pump();
    } catch {
      phase.value = 'denied';
    }
  }

  function setMode(m: ScanMode): void {
    mode.value = m;
    suggestGuide.value = false;
    voting.reset();
    vote.value = null;
    overlay.box = null;
    prevBox = null;
  }

  async function rescan(): Promise<void> {
    locked.value = null;
    voting.reset();
    vote.value = null;
    overlay.box = null;
    prevBox = null;
    phase.value = 'scanning';
    await videoRef.value?.play().catch(() => undefined);
  }

  /**
   * Freeze the scanner while another view (the details screen) is on top. The
   * worker — and its loaded models — stay alive; only the camera + frame pump
   * stop, so returning is instant instead of re-downloading/re-initializing the
   * models. Paired with resume() via KeepAlive in ScanView.
   */
  function pause(): void {
    pumpStopped = true;
    videoRef.value?.pause();
    camera.value?.stop();
  }

  async function resume(): Promise<void> {
    if (!camera.value) return; // camera was never enabled (manual-lookup path)
    try {
      await camera.value.start();
      await rescan();
      pumpStopped = false;
      pump();
    } catch {
      phase.value = 'denied';
    }
  }

  async function toggleFavorite(): Promise<void> {
    const l = locked.value;
    if (!l || !lastSaved || lastSaved.id !== l.savedId || l.savedId === null) return;
    lastSaved.favorite = !lastSaved.favorite;
    l.favorite = lastSaved.favorite;
    await updateScan(lastSaved);
  }

  /** Persist a hand-corrected plate text onto the auto-recorded scan. */
  async function applyEdit(text: string): Promise<void> {
    const l = locked.value;
    if (!l) return;
    if (text !== l.text) {
      l.text = text;
      if (lastSaved && lastSaved.id === l.savedId && l.savedId !== null) {
        lastSaved.text = text;
        lastSaved.edited = true;
        await updateScan(lastSaved);
      }
    }
  }

  function destroy(): void {
    pumpStopped = true;
    worker?.terminate();
    camera.value?.destroy();
  }

  return {
    phase,
    mode,
    errorMsg,
    modelProgress,
    camState,
    camera,
    vote,
    locked,
    overlay,
    debug,
    suggestGuide,
    lockFlash,
    guideRectDisplay,
    elemSize,
    begin,
    requestCamera,
    setMode,
    rescan,
    pause,
    resume,
    toggleFavorite,
    applyEdit,
    destroy,
  };
}
